/**
 * The bid's insight fields (summary, criteria, win themes, questions, plans)
 * are stored in whatever shape produced them: plain text, markdown, HTML
 * from the web editors, or a JSON string wrapping one of those (and, for
 * older bids, Plate nodes). The pane shows them as readable text: block
 * elements become lines, list items become bullets, tags and JSON go.
 */

type PlateNode = { text?: string; children?: PlateNode[]; type?: string };

const plateText = (nodes: PlateNode[]): string =>
  nodes
    .map((node) => {
      if (typeof node.text === "string") return node.text;
      const inner = node.children ? plateText(node.children) : "";
      const isItem = node.type === "li" || node.type === "list_item";
      return (isItem ? `• ${inner}` : inner) + "\n";
    })
    .join("")
    .replace(/\n{3,}/g, "\n\n");

const unwrapJson = (raw: string): string => {
  const trimmed = raw.trim();
  if (!/^[[{"]/.test(trimmed)) return raw;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === "string") return unwrapJson(parsed);
    if (Array.isArray(parsed)) return plateText(parsed as PlateNode[]);
    if (parsed && typeof parsed === "object") {
      const record = parsed as Record<string, unknown>;
      for (const key of ["content", "html", "text", "markdown", "value", "summary"]) {
        const value = record[key];
        if (typeof value === "string") return unwrapJson(value);
        if (Array.isArray(value)) return plateText(value as PlateNode[]);
      }
      return Object.values(record)
        .filter((v): v is string => typeof v === "string")
        .join("\n\n");
    }
  } catch {
    // not JSON after all
  }
  return raw;
};

const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "UL",
  "OL",
  "TABLE",
  "TR",
  "BLOCKQUOTE",
  "SECTION",
]);

const htmlToText = (html: string): string => {
  if (typeof DOMParser === "undefined") return html.replace(/<[^>]+>/g, "");
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out: string[] = [];
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push((node.textContent ?? "").replace(/\s+/g, " "));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName;
    if (tag === "SCRIPT" || tag === "STYLE") return;
    if (tag === "BR") {
      out.push("\n");
      return;
    }
    if (tag === "LI") out.push("\n• ");
    else if (BLOCK_TAGS.has(tag)) out.push("\n");
    if (/^H[1-6]$/.test(tag)) out.push("");
    el.childNodes.forEach(walk);
    if (BLOCK_TAGS.has(tag) || tag === "LI") out.push("\n");
    if (tag === "TD" || tag === "TH") out.push("  ");
  };
  doc.body.childNodes.forEach(walk);
  return out.join("");
};

const stripMarkdown = (text: string): string =>
  text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/(^|\s)\*([^*\n]+)\*(?=\s|$)/g, "$1$2")
    .replace(/^\s*[-*+]\s+/gm, "• ")
    .replace(/^\s*(\d+)\.\s+/gm, "$1. ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/`([^`]+)`/g, "$1");

/**
 * Readable text for an insight field, or "" when it is empty.
 * @param raw - The stored value.
 */
export const insightText = (raw: unknown): string => {
  if (raw === null || raw === undefined) return "";
  let text = typeof raw === "string" ? raw : Array.isArray(raw) ? plateText(raw as PlateNode[]) : String(raw);
  text = unwrapJson(text);
  if (/<\/?[a-z][\s\S]*>/i.test(text)) text = htmlToText(text);
  text = stripMarkdown(text);
  return text
    .split("\n")
    .map((line) => line.replace(/[ \t]+$/g, "").replace(/^ +(?=•)/, ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
